// src/campaigns/providers/CampaignProvider.tsx

import React, { createContext, useContext, useMemo } from "react";
import { CAMPAIGNS } from "../configs";
import {
  getActiveCampaign,
  getCampaignById,
  getCampaignBySlug,
  getNextCampaign,
} from "../engine/campaign-helpers";
import { CampaignDefinition } from "../types/campaign.types";

interface CampaignContextValue {
  activeCampaign: CampaignDefinition | null;
  nextCampaign: CampaignDefinition | null;
  campaigns: CampaignDefinition[];
  getCampaignBySlug: (slug?: string | null) => CampaignDefinition | null;
  getCampaignById: (id?: string | null) => CampaignDefinition | null;
}

const CampaignContext = createContext<CampaignContextValue | undefined>(undefined);

export function CampaignProvider({ children }: { children: React.ReactNode }) {
  const activeCampaign = useMemo(() => getActiveCampaign(CAMPAIGNS), []);
  const nextCampaign = useMemo(() => getNextCampaign(CAMPAIGNS), []);

  const value = useMemo<CampaignContextValue>(
    () => ({
      activeCampaign,
      nextCampaign,
      campaigns: CAMPAIGNS,
      getCampaignBySlug: (slug?: string | null) => getCampaignBySlug(CAMPAIGNS, slug),
      getCampaignById: (id?: string | null) => getCampaignById(CAMPAIGNS, id),
    }),
    [activeCampaign, nextCampaign]
  );

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
}

export function useCampaign(): CampaignContextValue {
  const context = useContext(CampaignContext);

  if (!context) {
    return {
      activeCampaign: getActiveCampaign(CAMPAIGNS),
      nextCampaign: getNextCampaign(CAMPAIGNS),
      campaigns: CAMPAIGNS,
      getCampaignBySlug: (slug?: string | null) => getCampaignBySlug(CAMPAIGNS, slug),
      getCampaignById: (id?: string | null) => getCampaignById(CAMPAIGNS, id),
    };
  }

  return context;
}
